<template>
	<div class="cassie-vertical-md">
		<div
			v-if="driftWarning"
			class="drift-warning"
			role="status"
			:data-testid="`pass-list-${paramType}-drift-warning`"
		>
			{{ driftWarning }}
		</div>
		<div
			v-if="!passes.length"
			class="empty-state"
			:data-testid="`pass-list-${paramType}-empty`"
		>
			{{ 'No passes configured. Add a pass to get started.' | useLabels }}
		</div>

		<PassCard
			v-for="(pass, index) in passes"
			:key="pass._uiKey"
			:pass="pass"
			:is-first="index === 0"
			:is-last="index === passes.length - 1"
			:user-full-permissions="userFullPermissions"
			@update:updateDetails="onPassUpdateDetails(index, $event)"
			@move-up="movePass(index, index - 1)"
			@move-down="movePass(index, index + 1)"
			@add-rule="openAddRule(index)"
			@edit-rule="onEditRule(index, $event)"
			@duplicate-rule="onDuplicateRule(index, $event)"
			@toggle-rule-active="onToggleRuleActive(index, $event)"
		/>

		<AddEditRuleModal
			v-if="showRuleModal"
			:rule-to-edit="ruleBeingEdited"
			:field-name-items="fieldNameItems"
			:max-pass-number="maxPassNumberForModal"
			:default-merge-pass="defaultMergePassForModal"
			@cancel="closeRuleModal"
			@save="saveRuleFromModal"
		/>
	</div>
</template>

<script>
import PassCard from './PassCard.vue'
import AddEditRuleModal from './AddEditRuleModal.vue'
import { groupRulesByPass, flattenPasses } from '../utils/group-rules-by-pass.js'

export default {
	name: 'pass-list',
	components: { PassCard, AddEditRuleModal },
	props: {
		matchRules: { type: Array, default: () => [] },
		fieldNameItems: { type: Array, default: () => [] },
		userFullPermissions: { type: Boolean, default: false },
		paramType: { type: String, required: true }
	},
	data () {
		return {
			passes: [],
			showRuleModal: false,
			ruleBeingEdited: null,
			editingPassIndex: null,
			editingRuleIndex: null
		}
	},
	computed: {
		driftWarning () {
			const inconsistent = this.passes.filter(p => p.updateDetailsWasInconsistent)
			if (inconsistent.length === 0) return ''
			const passWord = this.$options.filters.useLabels('Pass')
			const list = inconsistent.map(p => `${passWord} ${p.mergePass}`).join(', ')
			const message = this.$options.filters.useLabels(
				'had inconsistent "Update details" values across rules. Values shown reflect the canonical (lowest-id) rule and will be normalised on next save.'
			)
			return `${list} ${message}`
		},
		maxPassNumberForModal () {
			return Math.max(this.passes.length, 1)
		},
		defaultMergePassForModal () {
			return this.editingPassIndex != null
				? this.passes[this.editingPassIndex].mergePass
				: this.passes.length + 1
		}
	},
	watch: {
		matchRules: {
			immediate: true,
			handler (val) {
				if (this.showRuleModal) this.resetRuleModalState()
				this.passes = groupRulesByPass(val)
			}
		}
	},
	methods: {
		emitFlat () {
			this.$emit('change', flattenPasses(this.passes))
		},
		onPassUpdateDetails (passIndex, value) {
			this.$set(this.passes[passIndex], 'updateDetails', value)
			this.$set(this.passes[passIndex], 'updateDetailsWasInconsistent', false)
			this.emitFlat()
		},
		movePass (from, to) {
			if (to < 0 || to >= this.passes.length) return
			const copy = [...this.passes]
			const [moved] = copy.splice(from, 1)
			copy.splice(to, 0, moved)
			copy.forEach((p, idx) => { p.mergePass = idx + 1 })
			this.passes = copy
			this.emitFlat()
		},
		addPass () {
			this.passes.push({
				mergePass: this.passes.length + 1,
				updateDetails: false,
				updateDetailsWasInconsistent: false,
				rules: [],
				_uiKey: this.generateUiKey()
			})
			this.openAddRule(this.passes.length - 1)
		},
		generateUiKey () {
			return `pass-uikey-local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
		},
		openAddRule (passIndex) {
			this.editingPassIndex = passIndex
			this.editingRuleIndex = null
			this.ruleBeingEdited = null
			this.showRuleModal = true
		},
		onEditRule (passIndex, ruleIndex) {
			this.editingPassIndex = passIndex
			this.editingRuleIndex = ruleIndex
			this.ruleBeingEdited = {
				...this.passes[passIndex].rules[ruleIndex],
				mergePass: this.passes[passIndex].mergePass,
				updateDetails: this.passes[passIndex].updateDetails
			}
			this.showRuleModal = true
		},
		closeRuleModal () {
			if (
				this.editingPassIndex != null &&
				this.editingRuleIndex == null &&
				this.passes[this.editingPassIndex] &&
				this.passes[this.editingPassIndex].rules.length === 0
			) {
				this.passes.splice(this.editingPassIndex, 1)
				this.passes.forEach((p, idx) => { p.mergePass = idx + 1 })
			}
			this.resetRuleModalState()
		},
		resetRuleModalState () {
			this.showRuleModal = false
			this.editingPassIndex = null
			this.editingRuleIndex = null
			this.ruleBeingEdited = null
		},
		saveRuleFromModal (formRule) {
			// Keep every field the rule carries — notably `brand`, which isn't shown in
			// the modal — by stripping only the pass-level fields. flattenPasses re-derives
			// mergePass/updateDetails at save time, so dropping them here is correct, and
			// rebuilding from a fixed field list would silently reset brand to 0 on edit.
			const { mergePass, updateDetails, ...newRule } = formRule

			if (this.editingRuleIndex != null) {
				const oldPass = this.passes[this.editingPassIndex]

				// In-place edit when the pass didn't change.
				if (oldPass.mergePass === formRule.mergePass) {
					this.$set(oldPass.rules, this.editingRuleIndex, newRule)
					this.resetRuleModalState()
					this.emitFlat()
					return
				}

				// Pass changed. Resolve the destination pass BY REFERENCE before mutating
				// the array — removing an emptied source pass renumbers the others, which
				// would invalidate a mergePass-based lookup performed afterwards.
				const targetPass = this.passes.find(p => p.mergePass === formRule.mergePass)
				oldPass.rules.splice(this.editingRuleIndex, 1)

				if (targetPass) {
					targetPass.rules.push(newRule)
				} else {
					this.passes.push({
						mergePass: this.passes.length + 1,
						updateDetails: false,
						updateDetailsWasInconsistent: false,
						rules: [newRule],
						_uiKey: this.generateUiKey()
					})
				}

				// Drop the source pass if the move emptied it, then renumber contiguously.
				if (oldPass.rules.length === 0) {
					this.passes.splice(this.passes.indexOf(oldPass), 1)
					this.passes.forEach((p, idx) => { p.mergePass = idx + 1 })
				}

				this.resetRuleModalState()
				this.emitFlat()
				return
			}

			// Add path — no source pass to clean up.
			const targetPassIndex = this.passes.findIndex(p => p.mergePass === formRule.mergePass)
			if (targetPassIndex >= 0) {
				this.passes[targetPassIndex].rules.push(newRule)
			} else {
				this.passes.push({
					mergePass: this.passes.length + 1,
					updateDetails: false,
					updateDetailsWasInconsistent: false,
					rules: [newRule],
					_uiKey: this.generateUiKey()
				})
			}
			this.resetRuleModalState()
			this.emitFlat()
		},
		onDuplicateRule (passIndex, ruleIndex) {
			const original = this.passes[passIndex].rules[ruleIndex]
			this.passes[passIndex].rules.splice(ruleIndex + 1, 0, { ...original, id: null })
			this.emitFlat()
		},
		onToggleRuleActive (passIndex, ruleIndex) {
			const rule = this.passes[passIndex].rules[ruleIndex]
			this.$set(rule, 'isActive', !rule.isActive)
			this.emitFlat()
		}
	}
}
</script>

<style scoped>
.empty-state {
	padding: var(--spacing-xl);
	text-align: center;
	color: var(--muted-foreground);
	background: var(--muted);
	border: 1px dashed var(--border);
	border-radius: var(--radius);
}
.drift-warning {
	font-size: var(--font-size-sm);
	color: var(--warning);
	background: var(--warning-light);
	padding: 6px 10px;
	border-radius: var(--radius-sm);
	margin-left: var(--spacing-md);
}
</style>
